import { usePagination } from "@/hooks/use-pagination"
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "@/components/ui/pagination"

type PaginationComponentProps = {
    currentPage: number
    totalPages: number
    onPageChange: (page: number) => void
    paginationItemsToDisplay?: number
}

export default function PaginationComponent({
    currentPage,
    totalPages,
    onPageChange,
    paginationItemsToDisplay = 5,
}: PaginationComponentProps) {
    const { pages, showLeftEllipsis, showRightEllipsis } = usePagination({
        currentPage,
        totalPages,
        paginationItemsToDisplay,
    })

    const handlePrevious = (e: React.MouseEvent) => {
        e.preventDefault()
        if (currentPage > 1) {
            onPageChange(currentPage - 1)
        }
    }

    const handleNext = (e: React.MouseEvent) => {
        e.preventDefault()
        if (currentPage < totalPages) {
            onPageChange(currentPage + 1)
        }
    }

    const handlePageClick = (e: React.MouseEvent, page: number) => {
        e.preventDefault()
        onPageChange(page)
    }

    // Always render the component - parent handles visibility logic

    return (
        <Pagination>
            <PaginationContent>
                {/* Previous page button */}
                <PaginationItem>
                    <PaginationPrevious
                        onClick={handlePrevious}
                        className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                        aria-disabled={currentPage === 1}
                    />
                </PaginationItem>

                {/* Left ellipsis (...) */}
                {showLeftEllipsis && (
                    <PaginationItem>
                        <PaginationEllipsis />
                    </PaginationItem>
                )}

                {/* Page number links */}
                {pages.map((page) => (
                    <PaginationItem key={page}>
                        <PaginationLink
                            onClick={(e) => handlePageClick(e, page)}
                            isActive={page === currentPage}
                            className="cursor-pointer"
                        >
                            {page}
                        </PaginationLink>
                    </PaginationItem>
                ))}

                {/* Right ellipsis (...) */}
                {showRightEllipsis && (
                    <PaginationItem>
                        <PaginationEllipsis />
                    </PaginationItem>
                )}

                {/* Next page button */}
                <PaginationItem>
                    <PaginationNext
                        onClick={handleNext}
                        className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                        aria-disabled={currentPage === totalPages}
                    />
                </PaginationItem>
            </PaginationContent>
        </Pagination>
    )
}
